import type {ExemplarRequest, Identified, LabeledReference, ListResult, Rec, RemoteValidationDto, UUID, User, Validity, Valuable} from 'src/typings';
import type {ValueEditorConfigurationManagerProvider} from './configuration/value-editor-configuration-manager.provider';
import type {KpValueEditorConfigurationService} from './kp-value-editor/kp-value-editor-configuration.service';
import type {ValueEditorAliasesService} from './aliases/value-editor-aliases.service';
import RecordAsLinkModel from './internal/editors/search-or-edit/templates/RecordAsLinkModel.svelte';
import KpSelectedUserCard from './internal/editors/search-or-edit/templates/KpSelectedUserCard.svelte';
import RecordAsEmptyModel from './internal/editors/search-or-edit/templates/RecordAsEmptyModel.svelte';
import KpSelectableRecordCard from './internal/editors/search-or-edit/templates/KpSelectableRecordCard.svelte';
import KpPreformattedText from './internal/editors/search-or-edit/templates/KpPreformattedText.svelte';
import type {Field7ValueEditorService} from './custom-editors-services/field7-value-editor.service';
import type {Field8ValueEditorService} from './custom-editors-services/field8-value-editor.service';
import type SearchableValueEditorService from './custom-editors-services/searchable-value-editor.service';
import type {LogService} from 'core/logging/log.service';
import type {KpUniversalFormConfigurationService} from './kp-universal-form/kp-universal-form-configuration.service';
import {transferify} from '../utils/data-service-utils';
import type UserDataService from '../../features/user/user.data-service';
import type ExemplarDataService from '../../features/record/exemplar.data-service';
import type {SvelteComponentConstructor} from 'core/types';
import type {GenerableTextValueEditorAdditionalRequestParameters} from './internal/editors/generable-text/types';
import type RecordService from '../../features/record/record.service';
import {DEFAULT_CURRENCY} from '../constants';
import type {AuthorityField8ValueEditorService} from './custom-editors-services/authority-field8-value-editor.service';
import KpSelectableUserCard from './internal/editors/search-or-edit/templates/KpSelectableUserCard.svelte';
import type {AjaxService} from 'core/data-services/ajax.service';
import {resolveErrorMessage} from 'shared/utils/error-utils';

/*@ngInject*/
export function valueEditorsOptionsConfig(valueEditorConfigurationManagerProvider: ValueEditorConfigurationManagerProvider,
                                          valueEditorConfigurationService: KpValueEditorConfigurationService,
                                          valueEditorAliasesService: ValueEditorAliasesService,
                                          universalFormConfigurationService: KpUniversalFormConfigurationService,
                                          field7ValueEditorService: Field7ValueEditorService,
                                          field8ValueEditorService: Field8ValueEditorService,
                                          authorityField8ValueEditorService: AuthorityField8ValueEditorService,
                                          recordService: RecordService,
                                          searchableValueEditorService: SearchableValueEditorService,
                                          userDataService: UserDataService,
                                          exemplarDataService: ExemplarDataService,
                                          ajaxService: AjaxService,
                                          logService: LogService) {

    universalFormConfigurationService.autofocusFirstField = true;
    valueEditorConfigurationService.setDebugMode(false);

    valueEditorAliasesService.addAlias('search-text', 'text-with-metadata');

    valueEditorAliasesService.addAlias('velocity-template', 'text');
    valueEditorConfigurationManagerProvider.getManagerForType('text').forAlias('velocity-template').setConfiguration({
        type: 'rich-textarea'
    });

    valueEditorConfigurationManagerProvider.getManagerForType('acceptable-root').setConfiguration({
        optionIdResolver: ({option}: {option: Identified<any>}) => option.id,
    });

    valueEditorConfigurationManagerProvider.getManagerForType('single-acceptable').setConfiguration({
        optionIdResolver: ({option}: {option: Identified<any>}) => option.id,
        optionDisabledResolver: ({item}: {item: {enabled?: boolean}}) => !(item?.enabled ?? true)
    });

    valueEditorConfigurationManagerProvider.getManagerForType('multiple-acceptable').setConfiguration({
        optionIdResolver: ({option}: {option: Identified<any>}) => option.id,
        optionDisabledResolver: ({item}: {item: {enabled?: boolean}}) => !(item?.enabled ?? true)
    });

    valueEditorConfigurationManagerProvider.getManagerForType('autocomplete').setConfiguration({
        emptyAsNull: true,
        dataSource: async ({staticParams}) => {
            const listResult = await ajaxService.withoutBaseUrl().createRequest(staticParams.remoteDataSourceUrl).get<ListResult<Valuable<string>>>();
            return listResult.content.map((item) => item.value);
        }
    });

    interface GenerableTextValueEditorRequestParameters {
        readableDepartmentsIds?: number[];
        originDepartmentId?: number;
    }

    valueEditorConfigurationManagerProvider.getManagerForType('generable-text').setConfiguration({
        emptyAsNull: true,
        requestFunction: ({requestParameters, additionalParameters}: {requestParameters: GenerableTextValueEditorRequestParameters, additionalParameters?: GenerableTextValueEditorAdditionalRequestParameters}) => {
            if (additionalParameters?.inputName === 'cardNumber') {
                return userDataService.getCardNumberSequence(requestParameters.readableDepartmentsIds);
            } else {
                return userDataService.getReaderBarCodeSequence(requestParameters.originDepartmentId);
            }
        }
    });

    valueEditorConfigurationManagerProvider.getManagerForType('password').setConfiguration({
        emptyAsNull: true
    });

    valueEditorConfigurationManagerProvider.getManagerForType('text').setConfiguration({
        emptyAsNull: true
    });

    valueEditorConfigurationManagerProvider.getManagerForType('range').setConfiguration({
        extremesAsNull: true,
        emptyAsNull: false
    });

    valueEditorConfigurationManagerProvider.getManagerForType('price').setConfiguration({
        emptyAsNull: true,
        currencyOptions: [DEFAULT_CURRENCY],
    });

    valueEditorAliasesService.addAlias('access-number', 'text-or-select');
    valueEditorConfigurationManagerProvider.getManagerForType('text-or-select').forAlias('access-number').setConfiguration({ // test aliased editor
        disableRequireIfFormContains: 'issue',
        emptyAsNull: true,
        dataSource: ({formModel, name, model}) =>
            exemplarDataService.getSequences(name, model, transferify(formModel))
                .then((sequence: string[]) => Array.from(new Set(sequence)))
    });

    valueEditorAliasesService.addAlias('signature', 'text-or-select');
    valueEditorConfigurationManagerProvider.getManagerForType('text-or-select').forAlias('signature').setConfiguration({ // test aliased editor
        emptyAsNull: true,
        dataSource: ({formModel, name, model}) =>
            exemplarDataService.getSequences(name, model, transferify(formModel))
                .then((sequence: string[]) => Array.from(new Set(sequence)))
    });

    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').setConfiguration({
        modelComponent: RecordAsLinkModel as SvelteComponentConstructor<{value: Rec}>,
        searchModelFunction: ({searchParams}) => searchableValueEditorService.searchAndSelectItem(searchParams)
    });

    valueEditorAliasesService.addAlias('user', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('user').setConfiguration({
        modelComponent: KpSelectedUserCard as SvelteComponentConstructor<{value: User}>,
        searchModelFunction: ({searchParams}) => {
            return searchableValueEditorService.searchAndSelectItem<User>(searchParams, KpSelectableUserCard as SvelteComponentConstructor<{value: User, isSelected: boolean}>);
        }
    });

    valueEditorAliasesService.addAlias('editable-record-search', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('editable-record-search').setConfiguration({
        modelComponent: RecordAsEmptyModel as SvelteComponentConstructor<{value: LabeledReference<UUID>}>,
        searchModelFunction: ({searchParams, createParams}) => recordService.findRecordOrCreateRecordWithHolding(searchParams, createParams),
        editModelFunction: ({model, editParams}) => recordService.createRecordEditation(editParams, model)
    });

    valueEditorAliasesService.addAlias('record-search', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('record-search').setConfiguration({
        modelComponent: KpSelectableRecordCard as SvelteComponentConstructor<{value: Rec}>,
        searchModelFunction: ({searchParams}) => searchableValueEditorService.searchAndSelectItem<Rec>(searchParams)
    });

    valueEditorAliasesService.addAlias('field7', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('field7').setConfiguration({
        modelComponent: KpPreformattedText as SvelteComponentConstructor<{value: string}>,
        searchModelFunction: null, // disable searching (to hide search button)
        editModelFunction: ({model, editParams}) => field7ValueEditorService.editModelFunction(model, editParams)
    });

    valueEditorAliasesService.addAlias('field8', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('field8').setConfiguration({
        modelComponent: KpPreformattedText as SvelteComponentConstructor<{value: string}>,
        searchModelFunction: null, // disable searching (to hide search button)
        editModelFunction: ({model, editParams}) => field8ValueEditorService.editModelFunction(model, editParams)
    });

    valueEditorAliasesService.addAlias('authority-field8', 'search-or-edit');
    valueEditorConfigurationManagerProvider.getManagerForType('search-or-edit').forAlias('authority-field8').setConfiguration({
        modelComponent: KpPreformattedText as SvelteComponentConstructor<{value: string}>,
        searchModelFunction: null, // disable searching (to hide search button)
        editModelFunction: ({model}) => authorityField8ValueEditorService.editModelFunction(model)
    });

    valueEditorConfigurationManagerProvider.getManagerForType('scannable-text').setConfiguration({
        emptyAsNull: true,
        autosubmit: true
    });

    valueEditorAliasesService.addAlias('smart-list', 'list');
    valueEditorConfigurationManagerProvider.getManagerForType('list').forAlias('smart-list').setConfiguration({
        sendWholeForm: true,
        onAddItem: ({formModel}) => {
            return exemplarDataService.getPrototypeOfExemplarRequestIdentifiers(formModel as unknown as ExemplarRequest)
        }
    });

    valueEditorConfigurationManagerProvider.getManagerForType('object').setConfiguration({
        labelsWidth: 3
    });

    valueEditorConfigurationService.setValidationFunction(
        /**
         *TODO: Tahle validacni funkce bude jeste asi chtit trochu potunit. Pravdepodobne pomoci RxJS, protoze zatim to hazi pro kazde
         * volani novy request, ale neceka se na vyrizeni stareho a ten stary se ani nerusi. Takze abychom "nesestrelili" server je
         * treba cancelovat dotazy. Ale nebude to tak ýzy, pac tohle je jedna über funkce na fsechny validace. Bude asi potreba to nejak
         * tridit, asi podle `fieldName` a | nebo `$additionalParameters.validationPathUrl`.
         */
        async ({fieldName, value, additionalParameters, form}) => {

            const route = additionalParameters.validationPathUrl;

            let payload: RemoteValidationDto<any> = {
                fieldName,
                value
            };

            if (form) {
                payload.formModel = transferify(form);
            }

            if (additionalParameters) {
                const additionalParams = {...additionalParameters};
                delete additionalParams.validationPathUrl;

                payload = {...additionalParameters, ...payload};
            }

            try {
                const validity = await ajaxService.withoutBaseUrl().createRequest(route).post<Validity>(payload);

                if (!validity.valid) {
                    return Promise.reject(validity.message ?? validity.type)
                }
                return true;

            } catch (error) {
                logService.error(resolveErrorMessage(error), error);
                return Promise.reject(resolveErrorMessage(error));
            }
        });
}
